import React, { useState, useEffect, useRef } from "react";
import "./GlobalNav.css";
import LoginModal from "./LoginModal";

const DEFAULT_LINKS = [
  { label: "Home", href: "/" },
  // { label: "About", href: "/about" },
  { label: "Music", href: "/music" },
  // { label: "Programs", href: "/programs" },
  // { label: "Events", href: "/events" },
  { label: "Boutique", href: "/shop" },
  { label: "Support", href: "/donate" },

];

/**
 * GlobalNav
 * Shared top nav for all public pages. Desktop shows inline links + CTA.
 * Mobile collapses links into a slide-down sheet behind a hamburger button.
 *
 * Props:
 *  - links: [{ label, href }]      (defaults to the 5 public routes)
 *  - activeHref: string            (highlights the matching link)
 *  - ctaLabel / onCtaClick: primary button, e.g. "Join the collective"
 *  - logoHref: where clicking the logo goes (default "/")
 */
export default function GlobalNav({
  links = DEFAULT_LINKS,
  activeHref,
  ctaLabel = "Join the collective",
  ctaHref = "/register",
  onCtaClick,
  logoHref = "/",
}) {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [cartQty, setCartQty] = useState(0);
  // Hub is only relevant once logged in — appended dynamically rather than
  // baked into DEFAULT_LINKS, so any page's custom `links` prop gets the
  // same behavior instead of needing to remember to add it themselves.
  const visibleLinks = currentUser
    ? [...links, { label: "Hub", href: "/hub" }]
    : links;
  const menuRef = useRef(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) setCurrentUser(JSON.parse(stored));
    } catch (e) {
      localStorage.removeItem("user");
    }
  }, []);

  // Same simple read-once-on-mount pattern the old Header-based pages
  // used (updateCartQuantity()) — not live-synced across tabs/components,
  // just restoring the same level of functionality that existed before.
  // Same simple read-once-on-mount pattern the old Header-based pages
  // used, PLUS a live listener for the 'cart:updated' event ShopPage.jsx
  // now fires after every add-to-cart action — without this, the badge
  // would only ever update on the next full page load/navigation, which
  // was the actual bug being reported (ShopPage wasn't touching
  // localStorage's 'cart' key at all before, so the count could never
  // move regardless of this listener).
  useEffect(() => {
    function readCart() {
      try {
        const savedCart = localStorage.getItem("cart");
        const cart = savedCart ? JSON.parse(savedCart) : [];
        setCartQty(Array.isArray(cart) ? cart.length : 0);
      } catch (e) {
        setCartQty(0);
      }
    }
    readCart();
    window.addEventListener("cart:updated", readCart);
    return () => window.removeEventListener("cart:updated", readCart);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  function handleLogout() {
    localStorage.removeItem("user");
    window.location.href = "/users/logout";
  }

  function handleLoginSuccess(user) {
    setCurrentUser(user);
    setLoginOpen(false);
  }

  function go(href) {
    setMenuOpen(false);
    window.location.href = href;
  }

  return (
    <header className="gnav">
      <div className="gnav-bar">
        <a href={logoHref} className="gnav-logo">MIHU</a>
        <div className="gnav-divider" />

        <nav className="gnav-links">
          {visibleLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={
                "gnav-link" + (activeHref === link.href ? " active" : "")
              }
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="gnav-spacer" />

        {currentUser ? (
          <div className="gnav-user" ref={menuRef}>
            <button
              className="gnav-avatar"
              aria-label="Open user menu"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {currentUser.photo ? (
                <img src={currentUser.photo} alt={currentUser.firstname || "Profile"} />
              ) : (
                <span>{(currentUser.firstname || currentUser.username || "?").charAt(0).toUpperCase()}</span>
              )}
            </button>

            {menuOpen && (
              <div className="gnav-dropdown" role="menu">
                <div className="gnav-dropdown-header">
                  {currentUser.firstname} {currentUser.lastname}
                </div>

                {/* Admin-only tools — same items as Hub.jsx's admin block */}
                {currentUser.admin && (
                  <>
                    <div className="gnav-dropdown-section">Admin</div>
                    <button className="gnav-dropdown-item" onClick={() => go("/registrations")}>
                      Approve registrations
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/record")}>
                      Record audio
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/audio")}>
                      Mix audio
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/voicemail")}>
                      Check messages
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/newAlbum")}>
                      Add album
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/newEpisode")}>
                      Add episode
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/post")}>
                      Post blog
                    </button>
                    <button className="gnav-dropdown-item" onClick={() => go("/calendar")}>
                      Event calendar
                    </button>
                    <div className="gnav-dropdown-divider" />
                  </>
                )}

                {/* Standard links — available to all logged-in users */}
                <div className="gnav-dropdown-section">Account</div>
                <button className="gnav-dropdown-item" onClick={() => go("/hub")}>
                  Hub
                </button>
                <button className="gnav-dropdown-item" onClick={() => go("/profile")}>
                  My profile
                </button>
                <button className="gnav-dropdown-item" onClick={() => go("/changePassword")}>
                  Change password
                </button>
                <div className="gnav-dropdown-divider" />
                <button className="gnav-dropdown-item gnav-dropdown-item--danger" onClick={handleLogout}>
                  Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="gnav-login" onClick={() => setLoginOpen(true)}>
            Log in
          </button>
        )}
                <a className="gnav-hotline" href="tel:6173974440" aria-label="Call the hotline">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span>Hotline&nbsp;&nbsp;</span>
        </a>

        {/* <a className="gnav-cta" href={ctaHref} onClick={onCtaClick}>
          {ctaLabel}
        </a> */}

        <a className="gnav-cart" href="/cart" aria-label="Cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          {cartQty > 0 && <span className="gnav-cart-badge">{cartQty}</span>}
        </a>

        <button
          className="gnav-burger"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {open && (
        <nav className="gnav-mobile-sheet">
          {visibleLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={
                "gnav-mobile-link" + (activeHref === link.href ? " active" : "")
              }
              onClick={() => setOpen(false)}
            >
              {link.label}
            </a>
          ))}
          {currentUser ? (
            <>
              <a href="/profile" className="gnav-mobile-link" onClick={() => setOpen(false)}>My profile</a>
              {currentUser.admin && (
                <a href="/calendar" className="gnav-mobile-link" onClick={() => setOpen(false)}>Event calendar</a>
              )}
              <button className="gnav-mobile-link gnav-mobile-logout" onClick={handleLogout}>
                Log out
              </button>
            </>
          ) : (
            <button
              className="gnav-mobile-link"
              onClick={() => { setOpen(false); setLoginOpen(true); }}
              style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", width: "100%" }}
            >
              Log in
            </button>
          )}
          <a
            className="gnav-mobile-link gnav-mobile-cart"
            href="/cart"
            onClick={() => setOpen(false)}
          >
            Cart{cartQty > 0 ? ` (${cartQty})` : ""}
          </a>
          <a
            className="gnav-cta gnav-mobile-cta"
            href={ctaHref}
            onClick={() => {
              setOpen(false);
              onCtaClick && onCtaClick();
            }}
          >
            {ctaLabel}
          </a>
        </nav>
      )}

      {loginOpen && (
        <LoginModal
          onClose={() => setLoginOpen(false)}
          onSuccess={handleLoginSuccess}
          redirectUrl="/hub"
        />
      )}
    </header>
  );
}
